import type { PropsWithChildren, ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import * as React from 'react'
import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import useMountedEffect from 'hooks/useMountedEffect'
import noop from 'lib/function/noop'
import debounce from 'lodash.debounce'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import { useIntl } from 'react-intl'

interface Props {
  heading?: string | ReactNode
  id?: string
  className?: string
  onInViewChange?: (id: string, inView: boolean) => void
  initialHeight: number
  minimumContentHeight?: number
  open?: boolean
  onToggle?: (id: string) => void
  collapsable?: boolean
  unconstrainedWhenExpanded?: boolean
  ref?: React.Ref<HTMLDivElement | null>
}

function NavigableOfferContentGroup(props: PropsWithChildren<Props>) {
  const {
    children,
    heading,
    id,
    onInViewChange = noop,
    initialHeight,
    minimumContentHeight,
    open,
    collapsable,
    unconstrainedWhenExpanded,
    onToggle = noop,
    ref,
  } = props
  const intl = useIntl()

  const [bottomOffset, setBottomOffset] = useState(0)

  const [viewRef, inView] = useInView({ rootMargin: `-60px 0px ${bottomOffset}px 0px` })

  useEffect(() => {
    const onResize = debounce(() => {
      // size of the header bar (60) + size of margin between items (20)
      const bottomOffset = 80 - window.innerHeight
      setBottomOffset(bottomOffset)
    }, 1000)

    window.addEventListener('resize', onResize)
    onResize()

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useMountedEffect(() => {
    onInViewChange(id, inView)
  }, [inView])

  const onViewMoreChange = useCallback(() => {
    onToggle(id)
  }, [id, onToggle])

  return (
    <div
      id={id}
      ref={(node) => {
        if (ref) {
          if (typeof ref === 'function') {
            ref(node)
          } else {
            ref.current = node
          }
        }
        viewRef(node)
      }}
    >
      <Heading variant="heading3">{heading}</Heading>
      <br />
      {collapsable && <LargeContentToggle
        open={open}
        maxHeight={initialHeight}
        showLessText={intl.formatMessage({
          id: 'jSBEi71Yg7RaeJyUcCyN',
          defaultMessage: 'Read less',
          description: 'Link text to collapse content in navigable offer content group',
        })}
        showMoreText={intl.formatMessage({
          id: 'KH7R4x3IzydelClifX3x',
          defaultMessage: 'Read more',
          description: 'Link text to expand content in navigable offer content group',
        })}
        onToggle={onViewMoreChange}
        minimumContentHeight={minimumContentHeight}
        unconstrainedWhenExpanded={unconstrainedWhenExpanded}
      >
        {children}
      </LargeContentToggle>}
      {!collapsable && children}
    </div>
  )
}

NavigableOfferContentGroup.displayName = 'NavigableOfferContentGroup'

export default NavigableOfferContentGroup
